import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  TrendingUp,
  Lock,
  Eye,
  Plus,
  Trash2,
  RefreshCw,
  User,
  Settings,
  Sparkles,
  Globe,
  AlertCircle,
  Loader2,
  Share2,
  History,
  Link,
  Unlink,
  Activity,
} from "lucide-react";
import { useApi } from "../hooks/useApi";
import { DashboardLayout } from "../components/DashboardLayout";

export const PrivacyPage = () => {
  const api = useApi();
  const [privacyScore, setPrivacyScore] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [sharingPermissions, setSharingPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [scoreRes, logsRes, sharingRes] = await Promise.all([
        api.get("/privacy-score"),
        api.get("/audit-logs?limit=20"),
        api.get("/sharing"),
      ]);
      setPrivacyScore(scoreRes.data);
      setAuditLogs(logsRes.data);
      setSharingPermissions(sharingRes.data);
    } catch (e) {
      toast.error("Failed to load privacy data");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const revokePermission = async (id) => {
    try {
      await api.delete(`/sharing/${id}`);
      toast.success("Permission revoked");
      loadData();
    } catch (e) {
      toast.error("Failed to revoke");
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'create': return <Plus className="w-4 h-4 text-green-400" />;
      case 'delete': return <Trash2 className="w-4 h-4 text-red-400" />;
      case 'update': return <RefreshCw className="w-4 h-4 text-yellow-400" />;
      case 'view': return <Eye className="w-4 h-4 text-blue-400" />;
      case 'share': return <Share2 className="w-4 h-4 text-purple-400" />;
      case 'valuate': return <Sparkles className="w-4 h-4 text-cyan-400" />;
      case 'access': return <User className="w-4 h-4 text-gray-400" />;
      default: return <Activity className="w-4 h-4 text-gray-400" />;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto" data-testid="privacy-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Privacy & Security</h1>
          <p className="text-gray-400 mt-1">Monitor and control your data privacy</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Privacy Score */}
          <div className="lg:col-span-1">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20 sticky top-4">
              <h2 className="text-lg font-semibold text-white mb-4">Privacy Score</h2>
              <div className="text-center mb-6">
                <div className={`w-32 h-32 rounded-full mx-auto flex items-center justify-center border-4 ${
                  privacyScore?.overall_score >= 80 ? 'border-green-500' :
                  privacyScore?.overall_score >= 60 ? 'border-yellow-500' :
                  privacyScore?.overall_score >= 40 ? 'border-orange-500' : 'border-red-500'
                }`}>
                  <div>
                    <p className="text-4xl font-bold text-white">{privacyScore?.overall_score || 0}</p>
                    <p className="text-sm text-gray-400">/100</p>
                  </div>
                </div>
                <p className={`mt-3 font-medium ${
                  privacyScore?.rating === 'Excellent' ? 'text-green-400' :
                  privacyScore?.rating === 'Good' ? 'text-cyan-400' :
                  privacyScore?.rating === 'Fair' ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {privacyScore?.rating || 'Unknown'}
                </p>
              </div>

              {/* Factors */}
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Lock className="w-4 h-4 text-green-400" />
                    <span className="text-sm text-gray-300">Encryption</span>
                  </div>
                  <span className="text-green-400 text-sm font-medium">+10</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-300">Items for Sale</span>
                  </div>
                  <span className="text-red-400 text-sm font-medium">
                    {privacyScore?.factors?.items_for_sale?.impact || '0'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Globe className="w-4 h-4 text-gray-400" />
                    <span className="text-sm text-gray-300">Public Shares</span>
                  </div>
                  <span className="text-red-400 text-sm font-medium">
                    {privacyScore?.factors?.public_shares?.impact || '0'}
                  </span>
                </div>
                <div className="flex items-center justify-between p-3 bg-cyan-500/10 rounded-xl">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-cyan-400" />
                    <span className="text-sm text-gray-300">Valuations</span>
                  </div>
                  <span className="text-cyan-400 text-sm font-medium">
                    {privacyScore?.factors?.valuations_done?.impact || '0'}
                  </span>
                </div>
              </div>

              {/* Recommendations */}
              {privacyScore?.recommendations?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-purple-500/20">
                  <h3 className="text-sm font-medium text-gray-300 mb-2">Recommendations</h3>
                  <ul className="space-y-2">
                    {privacyScore.recommendations.map((rec) => (
                      <li key={rec} className="flex items-start gap-2 text-xs text-gray-400">
                        <AlertCircle className="w-3 h-3 text-yellow-400 mt-0.5" />
                        {rec}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          {/* Sharing Controls & Audit Log */}
          <div className="lg:col-span-2 space-y-6">
            {/* Data Sharing Controls */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
              <h2 className="text-lg font-semibold text-white mb-4">Data Sharing Controls</h2>
              {sharingPermissions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Share2 className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No active sharing permissions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {sharingPermissions.map((perm) => (
                    <div key={perm.id} className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-purple-500/10">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          perm.is_public ? 'bg-yellow-500/20' : 'bg-purple-500/20'
                        }`}>
                          {perm.is_public ? <Globe className="w-5 h-5 text-yellow-400" /> : <Link className="w-5 h-5 text-purple-400" />}
                        </div>
                        <div>
                          <p className="text-white font-medium">{perm.data_item_name || 'Data Item'}</p>
                          <p className="text-sm text-gray-400">
                            {perm.is_public ? 'Public' : perm.shared_with_email || 'Link sharing'} • {perm.permission_type}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => revokePermission(perm.id)}
                        className="flex items-center gap-1 px-3 py-1 text-red-400 hover:bg-red-500/20 rounded-lg transition text-sm"
                      >
                        <Unlink className="w-4 h-4" />
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Audit Log */}
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-purple-500/20">
              <h2 className="text-lg font-semibold text-white mb-4">Audit Log</h2>
              {auditLogs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No activity recorded yet</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {auditLogs.map((log) => (
                    <div key={log.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-purple-500/10">
                      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
                        {getActionIcon(log.action)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white capitalize">
                          {log.action} {log.resource_type?.replace('_', ' ')}
                        </p>
                        {log.details && (
                          <p className="text-xs text-gray-500 truncate">{log.details}</p>
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

// Settings Page
